import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Home,
  Check,
  X,
  Loader2,
  AlertTriangle,
  UserPlus
} from 'lucide-react';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Role configurations
const ROLES = {
  admin: { name: { en: 'Household Admin', es: 'Administrador' } },
  contributor: { name: { en: 'Contributor', es: 'Contribuidor' } },
  limited: { name: { en: 'Limited Member', es: 'Miembro Limitado' } },
  viewer: { name: { en: 'Viewer', es: 'Observador' } }
};

const JoinHouseholdPage = ({ inviteCode, token, isAuthenticated, onLoginRequired, onJoinSuccess }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Fetch invite info
  useEffect(() => {
    const fetchInviteInfo = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/household/invite/${inviteCode}/info`);
        
        if (response.ok) {
          const data = await response.json();
          setInviteInfo(data);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid invite');
        }
      } catch (err) {
        setError(lang === 'es' ? 'Error al cargar la invitación' : 'Error loading invite');
      }
      setLoading(false);
    };
    
    if (inviteCode) {
      fetchInviteInfo();
    }
  }, [inviteCode, lang]);
  
  // Handle join
  const handleJoin = async () => {
    if (!isAuthenticated) {
      onLoginRequired?.();
      return;
    }
    
    setJoining(true);
    try {
      const response = await fetch(`${API}/household/join/${inviteCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        setTimeout(() => {
          onJoinSuccess?.(data);
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error joining household');
      }
    } catch (err) {
      setError(lang === 'es' ? 'Error al unirse al hogar' : 'Error joining household');
    }
    setJoining(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error && !inviteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-700">
              {lang === 'es' ? 'Invitación Inválida' : 'Invalid Invite'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => window.location.href = '/'}>
              {lang === 'es' ? 'Ir al Inicio' : 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-green-700">
              {lang === 'es' ? '¡Te Uniste Exitosamente!' : 'Successfully Joined!'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600">
              {lang === 'es' 
                ? `Ahora eres miembro de "${inviteInfo?.household_name}"`
                : `You are now a member of "${inviteInfo?.household_name}"`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const roleConfig = ROLES[inviteInfo?.role] || ROLES.contributor;
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-10 h-10 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">
            {lang === 'es' ? 'Invitación Familiar' : 'Family Invitation'}
          </CardTitle>
          <CardDescription>
            {lang === 'es' 
              ? 'Has sido invitado a unirte a un hogar en ProsperMind'
              : 'You have been invited to join a household on ProsperMind'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Household Info */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                <Home className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="font-bold text-blue-900">{inviteInfo?.household_name}</h3>
                <p className="text-sm text-blue-700">
                  {lang === 'es' ? 'Hogar Familiar' : 'Family Household'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-700">{lang === 'es' ? 'Tu Rol:' : 'Your Role:'}</span>
              <span className="font-medium text-blue-900">{roleConfig.name[lang]}</span>
            </div>
          </div>
          
          {/* Benefits */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">
              {lang === 'es' ? 'Al unirte podrás:' : 'By joining you can:'}
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                {lang === 'es' ? 'Registrar gastos compartidos' : 'Log shared expenses'}
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                {lang === 'es' ? 'Ver presupuestos familiares' : 'View family budgets'}
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                {lang === 'es' ? 'Colaborar en metas financieras' : 'Collaborate on financial goals'}
              </li>
            </ul>
          </div>
          
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {/* Actions */}
          <div className="space-y-3">
            {isAuthenticated ? (
              <Button 
                onClick={handleJoin}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                disabled={joining}
              >
                {joining ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {lang === 'es' ? 'Uniéndose...' : 'Joining...'}
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    {lang === 'es' ? 'Unirme al Hogar' : 'Join Household'}
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-center text-gray-600">
                  {lang === 'es' 
                    ? 'Debes iniciar sesión o crear una cuenta para unirte'
                    : 'You need to login or create an account to join'}
                </p>
                <Button 
                  onClick={onLoginRequired}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                >
                  {lang === 'es' ? 'Iniciar Sesión / Registrarse' : 'Login / Sign Up'}
                </Button>
              </div>
            )}
            
            <Button 
              variant="ghost"
              onClick={() => window.location.href = '/'}
              className="w-full"
            >
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinHouseholdPage;
